import type { BlockDefinition } from '#layers/block-engine/types/block';
import type { BlockConfigApiBase } from './api/config';
import { callOrReturn, omitUndefined } from '#layers/base/public';
import { getComponentObjectProps } from '#layers/block-engine/utils/block/component-props';
import { getDefaultValuesFromFormSchema } from '#layers/block-engine/utils/form-schema/schema';
import { getBlockConfig } from './api/config';

/**
 * Build the initial prop bag for a freshly inserted block, layering (from least to most specific):
 * runtime component defaults → schema defaults from `defineBlock({ options })` → explicit caller overrides.
 */
export function getBlockInitialProps(options: {
	block: BlockDefinition;
	context: BlockConfigApiBase;
	props?: Record<string, unknown>;
	/** Translate string defaults before they are stored on the inserted block. */
	translateString: (value: string) => string;
}): Record<string, unknown> {
	const runtimeDefaults = getRuntimePropDefaults(options.block.comp);
	const config = getBlockConfig(options.block);
	const schemaDefaults = config.options
		? omitUndefined(getDefaultValuesFromFormSchema({
				schema: config.options,
				context: options.context,
				onDefaultError: () => {},
			}))
		: {};
	const translatedDefaults: Record<string, unknown> = {};

	for (const [name, value] of Object.entries({
		...runtimeDefaults,
		...schemaDefaults,
	})) {
		translatedDefaults[name] = translateBlockDefaultValue(value, options.translateString);
	}

	return {
		...translatedDefaults,
		...options.props,
	};
}

/**
 * Translate a string block default while preserving non-string values.
 */
export function translateBlockDefaultValue(
	value: unknown,
	translateString: (value: string) => string,
): unknown {
	return typeof value === 'string' ? translateString(value) : value;
}

/**
 * Resolves defaults for the initial serialized prop bag of a newly inserted block.
 *
 * Every default is evaluated with an empty props bag, including defaults for `Function` props, because this path
 * needs the runtime value Vue would use at insertion. Errors and `undefined` results are omitted: sibling props are
 * unavailable here, so a default like `default: (props) => props.other` cannot be resolved reliably.
 */
function getRuntimePropDefaults(component: BlockDefinition['comp']): Record<string, unknown> {
	const defaults: Record<string, unknown> = {};
	const props = getComponentObjectProps(component);

	if (!props) {
		return defaults;
	}

	for (const prop in props) {
		const options = props[prop];

		if (!options || typeof options !== 'object' || !('default' in options)) {
			continue;
		}

		try {
			defaults[prop] = callOrReturn(options.default, {});
		}
		catch {
			defaults[prop] = undefined;
		}
	}

	return omitUndefined(defaults);
}
